import { useEffect, useRef, useState } from 'react';

interface UseMultiTabSyncOptions {
  userId: string;
  isEnabled: boolean;
  onBecomeMaster?: () => void;
  onLoseMaster?: () => void;
}

export const useMultiTabSync = ({ userId, isEnabled, onBecomeMaster, onLoseMaster }: UseMultiTabSyncOptions) => {
  const [isMasterTab, setIsMasterTab] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const masterCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tabIdRef = useRef(Math.random().toString(36).substring(7));

  useEffect(() => {
    if (!isEnabled || !userId || typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channelName = `online-status-${userId}`;
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    let heartbeatInterval: NodeJS.Timeout | null = null;

    // Handle messages from other tabs
    channel.onmessage = (event) => {
      const { type, tabId, timestamp } = event.data;

      if (type === 'heartbeat' && tabId !== tabIdRef.current) {
        // Another tab is active and sending heartbeats
        if (isMasterTab) {
          // Check if we should yield master status
          const lastHeartbeat = localStorage.getItem(`master-heartbeat-${userId}`);
          if (lastHeartbeat && Date.now() - parseInt(lastHeartbeat) < 5000) {
            setIsMasterTab(false);
            onLoseMaster?.();
          }
        }
      }

      if (type === 'claim-master' && tabId !== tabIdRef.current) {
        // Another tab is claiming master status
        setIsMasterTab(false);
        onLoseMaster?.();
      }

      if (type === 'ping' && tabId !== tabIdRef.current) {
        // Respond to ping with pong
        channel.postMessage({ type: 'pong', tabId: tabIdRef.current, timestamp: Date.now() });
      }
    };

    // Function to claim master status
    const claimMaster = () => {
      channel.postMessage({ type: 'claim-master', tabId: tabIdRef.current, timestamp: Date.now() });
      localStorage.setItem(`master-heartbeat-${userId}`, Date.now().toString());
      setIsMasterTab(true);
      onBecomeMaster?.();
    };

    // Check if there's an existing master
    const checkForMaster = () => {
      return new Promise<boolean>((resolve) => {
        let hasResponse = false;
        
        const timeoutId = setTimeout(() => {
          if (!hasResponse) {
            resolve(false); // No master found
          }
        }, 100);

        const handlePong = (event: MessageEvent) => {
          if (event.data.type === 'pong' && event.data.tabId !== tabIdRef.current) {
            hasResponse = true;
            clearTimeout(timeoutId);
            channel.removeEventListener('message', handlePong);
            resolve(true); // Master exists
          }
        };

        channel.addEventListener('message', handlePong);
        channel.postMessage({ type: 'ping', tabId: tabIdRef.current, timestamp: Date.now() });
      });
    };

    // Initialize master status
    const initMaster = async () => {
      const masterExists = await checkForMaster();
      
      if (!masterExists) {
        claimMaster();
        
        // Send heartbeats as master
        heartbeatInterval = setInterval(() => {
          channel.postMessage({ type: 'heartbeat', tabId: tabIdRef.current, timestamp: Date.now() });
          localStorage.setItem(`master-heartbeat-${userId}`, Date.now().toString());
        }, 2000);
      }
    };

    initMaster();

    // Periodically check master status
    masterCheckIntervalRef.current = setInterval(async () => {
      if (!isMasterTab) {
        const lastHeartbeat = localStorage.getItem(`master-heartbeat-${userId}`);
        if (!lastHeartbeat || Date.now() - parseInt(lastHeartbeat) > 5000) {
          // Master seems to be gone, try to claim
          const masterExists = await checkForMaster();
          if (!masterExists) {
            claimMaster();
            
            if (heartbeatInterval) clearInterval(heartbeatInterval);
            heartbeatInterval = setInterval(() => {
              channel.postMessage({ type: 'heartbeat', tabId: tabIdRef.current, timestamp: Date.now() });
              localStorage.setItem(`master-heartbeat-${userId}`, Date.now().toString());
            }, 2000);
          }
        }
      }
    }, 3000);

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      
      if (masterCheckIntervalRef.current) {
        clearInterval(masterCheckIntervalRef.current);
      }

      if (isMasterTab) {
        localStorage.removeItem(`master-heartbeat-${userId}`);
      }

      channel.close();
    };
  }, [userId, isEnabled, isMasterTab]);

  return { isMasterTab };
};
